// features/projects/new-project-modal.js
// ═══════════════════════════════════════════════════════════
// Two-step new-project flow:
//   Step 1: pick type (Simple / Custom)
//   Step 2: title + emoji + one-line description
//
// On Create → POST /api/projects → onCreated(project) which
// drops the user into the projects 3-panel chat workspace.
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';

// Admin-gated templates — Continuum is Tim's private testbed
// per docs/PROFESSIONAL_TEMPLATE_SPEC.md §X. Public users see
// Standard + Professional only.
const ADMIN_EMAIL = 'timmai1004@gmail.com';

export default function NewProjectModal({ userId, userRecord, onClose, onCreated }) {
  const isAdmin = userRecord?.email === ADMIN_EMAIL;
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);
  const [template, setTemplate] = useState('standard');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [emoji, setEmoji] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  function pickType(t) {
    setType(t);
    // Reset template when switching back to Simple — only Custom carries
    // a template choice. Simple always uses the standard project shell.
    if (t === 'simple') setTemplate('standard');
    setStep(2);
  }

  async function submit() {
    if (!title.trim()) { setError('Please give your project a title.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          type,
          template,
          title: title.trim(),
          description: description.trim(),
          emoji: emoji.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to create project');
        setSubmitting(false);
        return;
      }
      onCreated(data.project);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <button style={S.modalClose} onClick={onClose} aria-label="Close">×</button>

        {step === 1 && (
          <>
            <div style={S.modalTitle}>Start a new project</div>
            <div style={S.modalSub}>What kind of project is this?</div>
            <div style={S.typeGrid}>
              <button style={S.typeCard} onClick={() => pickType('simple')}>
                <div style={S.typeIcon}>◈</div>
                <div style={S.typeName}>Simple</div>
                <div style={S.typeDesc}>
                  Personal experiments. Build a robot, prototype an IOT
                  device, try out an idea.
                </div>
                <div style={S.typeHint}>Setup: under 1 minute</div>
              </button>

              <button style={S.typeCard} onClick={() => pickType('custom')}>
                <div style={{ ...S.typeIcon, color: '#9b8fc4' }}>◇</div>
                <div style={S.typeName}>Custom</div>
                <div style={S.typeDesc}>
                  Research, conservation work, multi-stakeholder studies.
                  Shift will conduct an interview to capture goals + methodology.
                </div>
                <div style={S.typeHint}>Setup: title now, interview later</div>
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div style={S.modalEyebrow}>{type === 'custom' ? 'Custom Project' : 'Simple Project'}</div>
            <div style={S.modalTitle}>Name your project</div>

            <div style={S.fieldRow}>
              <div style={S.fieldGroup}>
                <label style={S.label}>Title <span style={{ color: '#c45050' }}>*</span></label>
                <input
                  style={S.input}
                  placeholder="e.g. Florida Everglades Conservation"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  autoFocus
                  maxLength={100}
                />
              </div>
              <div style={{ ...S.fieldGroup, flex: '0 0 70px' }}>
                <label style={S.label}>Emoji</label>
                <input
                  style={{ ...S.input, textAlign: 'center', fontSize: 18 }}
                  placeholder="◈"
                  value={emoji}
                  onChange={(e) => setEmoji(e.target.value)}
                  maxLength={4}
                />
              </div>
            </div>

            <div style={S.fieldGroup}>
              <label style={S.label}>One-line description</label>
              <textarea
                style={{ ...S.input, minHeight: 70, resize: 'vertical' }}
                placeholder="What is this project? (one line — Shift will dig deeper later)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={300}
              />
              <div style={S.charCount}>{description.length} / 300</div>
            </div>

            {/* Template selector — only Custom projects choose a template.
                Standard renders the existing 3-panel project workspace.
                Each named template (Continuum, neuro, etc.) renders its
                own dedicated workstation shell under templates/<id>/. */}
            {type === 'custom' && (
              <div style={S.fieldGroup}>
                <label style={S.label}>Template</label>
                <div style={S.templateGrid}>
                  <button
                    type="button"
                    onClick={() => setTemplate('standard')}
                    style={{
                      ...S.templateCard,
                      borderColor: template === 'standard' ? '#7a9e82' : 'var(--border-3, #272736)',
                      background: template === 'standard' ? 'rgba(122,158,130,.08)' : 'var(--surface-3, #171723)',
                    }}
                  >
                    <div style={{ ...S.templateName, color: template === 'standard' ? '#7a9e82' : '#bbb' }}>Standard</div>
                    <div style={S.templateDesc}>3-panel chat with Canvas + Journey. The default for any Custom project.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setTemplate('professional')}
                    style={{
                      ...S.templateCard,
                      borderColor: template === 'professional' ? '#c8a96e' : 'var(--border-3, #272736)',
                      background: template === 'professional' ? 'rgba(200,169,110,.08)' : 'var(--surface-3, #171723)',
                    }}
                  >
                    <div style={{ ...S.templateName, color: template === 'professional' ? '#c8a96e' : '#bbb' }}>Professional</div>
                    <div style={S.templateDesc}>Methodology workspace: Dashboard · Chat · Resources · Simulator. Composable widget grid for synthesis briefings.</div>
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => setTemplate('continuum')}
                      style={{
                        ...S.templateCard,
                        borderColor: template === 'continuum' ? '#9b8fc4' : 'var(--border-3, #272736)',
                        background: template === 'continuum' ? 'rgba(155,143,196,.08)' : 'var(--surface-3, #171723)',
                      }}
                    >
                      <div style={{ ...S.templateName, color: template === 'continuum' ? '#9b8fc4' : '#bbb' }}>
                        Continuum <span style={{ fontSize: 9, color: '#666', marginLeft: 6 }}>· admin</span>
                      </div>
                      <div style={S.templateDesc}>Tim's private testbed: 8-tab research workspace with the full methodology surface. Public patterns graduate to Professional.</div>
                    </button>
                  )}
                </div>
              </div>
            )}

            {error && <div style={S.error}>{error}</div>}

            <div style={S.modalActions}>
              <button style={S.btnSecondary} onClick={() => setStep(1)} disabled={submitting}>← Back</button>
              <button style={S.btnPrimary} onClick={submit} disabled={submitting || !title.trim()}>
                {submitting ? 'Creating…' : 'Create Project'}
              </button>
            </div>

            {type === 'custom' && (
              <div style={S.modalNote}>
                After creation, Shift will start the interview to capture
                goals, methodology, stakeholders, and resources.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const S = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(7,7,10,.85)',
    backdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
  },
  modal: {
    background: 'var(--surface-2, #111118)',
    border: '1px solid var(--border-2, #3a3a52)',
    borderRadius: 12,
    padding: '32px 32px 28px',
    width: 540,
    maxWidth: '90vw',
    maxHeight: '85vh',
    overflowY: 'auto',
    position: 'relative',
  },
  modalClose: {
    position: 'absolute',
    top: 14, right: 14,
    background: 'none',
    border: 'none',
    color: '#888',
    fontSize: 20,
    cursor: 'pointer',
    width: 28, height: 28,
  },
  modalEyebrow: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 9,
    letterSpacing: '.18em',
    textTransform: 'uppercase',
    color: '#9b8fc4',
    marginBottom: 8,
  },
  modalTitle: {
    fontFamily: 'Georgia, serif',
    fontSize: 24,
    fontWeight: 'bold',
    color: '#c8a96e',
    marginBottom: 6,
  },
  modalSub: {
    fontSize: 13,
    color: '#7a807e',
    marginBottom: 24,
    lineHeight: 1.55,
  },
  modalNote: {
    fontSize: 11,
    color: '#9b8fc4',
    background: 'rgba(155,143,196,.07)',
    border: '1px solid rgba(155,143,196,.18)',
    borderRadius: 6,
    padding: '10px 12px',
    marginTop: 14,
    lineHeight: 1.5,
  },
  typeGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    marginTop: 8,
  },
  typeCard: {
    background: 'var(--surface-3, #171723)',
    border: '1px solid var(--border-3, #272736)',
    borderRadius: 10,
    padding: '20px 18px',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'all .15s',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    color: 'var(--text, #e8e8f0)',
    fontFamily: "'DM Sans', sans-serif",
  },
  typeIcon: {
    fontSize: 24,
    color: '#7a9e82',
    lineHeight: 1,
    marginBottom: 4,
  },
  typeName: {
    fontFamily: 'Georgia, serif',
    fontSize: 17,
    fontWeight: 'bold',
    color: 'var(--text, #e8e8f0)',
  },
  typeDesc: {
    fontSize: 12,
    color: '#7a807e',
    lineHeight: 1.55,
    flex: 1,
  },
  typeHint: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 9,
    letterSpacing: '.1em',
    color: '#555',
    textTransform: 'uppercase',
    marginTop: 6,
  },
  fieldRow: {
    display: 'flex',
    gap: 10,
    marginBottom: 14,
  },
  fieldGroup: {
    flex: 1,
    marginBottom: 14,
  },
  label: {
    display: 'block',
    fontFamily: "'DM Mono', monospace",
    fontSize: 9,
    letterSpacing: '.14em',
    textTransform: 'uppercase',
    color: '#7a807e',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    background: 'var(--surface-3, #171723)',
    border: '1px solid var(--border-2, #3a3a52)',
    borderRadius: 6,
    padding: '10px 12px',
    color: 'var(--text, #e8e8f0)',
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    outline: 'none',
  },
  charCount: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 9,
    color: '#444',
    textAlign: 'right',
    marginTop: 4,
    letterSpacing: '.05em',
  },
  templateGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
  },
  templateCard: {
    border: '1px solid var(--border-3, #272736)',
    borderRadius: 6,
    padding: '10px 12px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'all .15s',
  },
  templateName: {
    fontFamily: 'Georgia, serif',
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 4,
  },
  templateDesc: {
    fontSize: 11,
    color: '#7a807e',
    lineHeight: 1.5,
  },
  error: {
    color: '#c45050',
    fontSize: 12,
    padding: '8px 10px',
    background: 'rgba(196,80,80,.06)',
    border: '1px solid rgba(196,80,80,.2)',
    borderRadius: 6,
    marginBottom: 12,
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    marginTop: 20,
  },
  btnPrimary: {
    background: 'rgba(122,158,130,.15)',
    border: '1px solid rgba(122,158,130,.4)',
    color: '#7a9e82',
    padding: '10px 22px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'Georgia, serif',
  },
  btnSecondary: {
    background: 'none',
    border: '1px solid var(--border-2, #3a3a52)',
    color: '#7a807e',
    padding: '10px 18px',
    borderRadius: 6,
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'Georgia, serif',
  },
};
